/**
 * Stat card row for the internal RMA list — quick-scan counts for the
 * most operationally important RMA buckets.
 *
 * Each card is clickable and sets a named bucket that drives compound
 * filter params in ReturnGrid. The bucket model lets a single click
 * express multi-field filter combos (e.g. status=0 AND is_quick_ship=true)
 * that would take multiple toolbar interactions otherwise.
 *
 * Contains:
 * - BUCKET_SEGMENTS  — ordered card definitions (label, color, bucket key)
 * - ReturnStatCards  — the responsive card row (default export)
 */
import { Box } from '@mui/material';
import { purple } from '@mui/material/colors';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';

/**
 * Named bucket definitions. Each bucket maps to a set of API params
 * applied to the RMA list (see ReturnGrid.extraParams).
 *
 * Bucket key 'all' clears any active bucket selection.
 */
export const BUCKET_SEGMENTS = [
  {
    key: 'all',
    label: 'All',
    labelKey: 'rma.bucketAll',
    countKey: 'all',
    color: undefined,
    tooltip: 'All RMAs matching the current scope and date filters.',
    tooltipKey: 'rma.bucketAllTooltip',
  },
  {
    key: 'awaiting_processing',
    label: 'Awaiting Processing',
    labelKey: 'rma.bucketAwaitingProcessing',
    countKey: 'awaiting_processing',
    color: 'info.main',
    tooltip: 'Issued RMAs (status=0) that are not quick-ship.',
    tooltipKey: 'rma.bucketAwaitingProcessingTooltip',
  },
  {
    key: 'quickship_pending',
    label: 'Quick-Ship Pending',
    labelKey: 'rma.bucketQuickShipPending',
    countKey: 'quickship_pending',
    color: 'warning.main',
    tooltip: 'Issued RMAs (status=0) that are flagged as quick-ship.',
    tooltipKey: 'rma.bucketQuickShipPendingTooltip',
  },
  {
    key: 'needs_photo',
    label: 'Needs Photo',
    labelKey: 'rma.bucketNeedsPhoto',
    countKey: 'needs_photo',
    color: purple[400],
    tooltip: 'RMAs awaiting a photo upload before they can be processed.',
    tooltipKey: 'rma.bucketNeedsPhotoTooltip',
  },
  {
    key: 'billing_needed',
    label: 'Billing Needed',
    labelKey: 'rma.bucketBillingNeeded',
    countKey: 'billing_needed',
    color: 'success.main',
    tooltip: 'RMAs marked ready to invoice.',
    tooltipKey: 'rma.bucketBillingNeededTooltip',
  },
  {
    key: 'delinquent',
    label: 'Delinquent',
    labelKey: 'rma.bucketDelinquent',
    countKey: 'delinquent',
    color: 'error.main',
    tooltip: 'Quick-ship RMAs that are overdue for processing.',
    tooltipKey: 'rma.bucketDelinquentTooltip',
  },
];

/**
 * ReturnStatCards — responsive row of clickable summary cards.
 *
 * Props:
 *   summary        – response from GET /rmas/summary/ (or null while loading)
 *   summaryLoading – boolean
 *   selectedBucket – the active bucket key string (or null for none)
 *   onBucketSelect – (key) => void; called with 'all' to clear
 */
export default function ReturnStatCards({ summary, summaryLoading, selectedBucket, onBucketSelect }) {
  const { t } = useTranslation();
  return (
    <StatCardGroup columns={{ xs: 2, sm: BUCKET_SEGMENTS.length }} sx={{ marginBottom: 'var(--page-gap)' }}>
      {BUCKET_SEGMENTS.map(segment => {
        const isActive = selectedBucket === segment.key || (segment.key === 'all' && !selectedBucket);
        const count = summary?.[segment.countKey] ?? 0;
        return (
          <StatCard
            key={segment.key}
            title={t(segment.labelKey, segment.label)}
            value={count}
            color={segment.color}
            tooltip={t(segment.tooltipKey, segment.tooltip)}
            loading={summaryLoading}
            active={isActive}
            onClick={() =>
              onBucketSelect(isActive && segment.key !== 'all' ? null : segment.key === 'all' ? null : segment.key)
            }
          />
        );
      })}
    </StatCardGroup>
  );
}
